import { Box, Chip, Tooltip } from '@mui/material';
import { Info } from 'lucide-react';
import { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Image3D } from '~/domain/patient/image3d';
import { useImageStreamStatus } from '~/hooks/image/useImageStreamProgress';
import { axesTiltDeg, formatAngle } from '~/utils/obliquity';
import { transform3DImageData } from '~/utils/transforms/imageDataTransforms';

const compactChipSx = {
    fontSize: 10,
    maxHeight: '12px',
    px: 0,
};

const INFO_ICON_SIZE = 10;

const tooltipAboveTooltipsSlotProps = {
    popper: {
        sx: {
            zIndex: (theme: { zIndex: { tooltip: number } }) =>
                theme.zIndex.tooltip + 1,
        },
    },
};

/**
 * "Still arriving", or "never arrived", on the image's own row in the list.
 *
 * Sits beside the interpolated-slices badge and must never be mistaken for it:
 * that badge is a small amber glyph meaning "synthetic anatomy"; this is a
 * filled chip carrying its own word and its own slice count, coloured `info`
 * while loading and `error` when retrieval ended short. Amber is left to the
 * interpolation badge alone.
 *
 * Renders nothing for a complete image, which is every image that did not come
 * from a progressive database open.
 */
function StreamingStatusChip({ image }: { image: Image3D }) {
    const status = useImageStreamStatus(image);

    if (!status) {
        return null;
    }

    const isFinal = status.kind === 'incomplete';

    return (
        <Tooltip title={status.description} arrow placement="top">
            <Chip
                label={status.label}
                size="small"
                color={isFinal ? 'error' : 'info'}
                aria-label={status.description}
                data-testid={
                    isFinal
                        ? 'image-item-incomplete-chip'
                        : 'image-item-loading-chip'
                }
                sx={compactChipSx}
            />
        </Tooltip>
    );
}

/**
 * The marks an image row carries about the image itself: its stream status,
 * interpolated slices, a frame of reference shared with the primary image,
 * and an oblique orientation with its angle. Each renders only when it
 * applies, so a plain image gets none.
 */
export default function ImageBadges({
    image,
    hasSharedFrameOfReference,
}: {
    image: Image3D;
    hasSharedFrameOfReference?: boolean;
}) {
    const intl = useIntl();
    const metadata = useMemo(() => transform3DImageData(image), [image]);

    const isOblique = !!image.firstSlice.tilted;
    const obliqueAngle = useMemo(
        () =>
            isOblique
                ? formatAngle(axesTiltDeg(image.firstSlice.imageOrientation))
                : null,
        [isOblique, image.firstSlice.imageOrientation]
    );

    return (
        <>
            <StreamingStatusChip image={image} />
            {metadata.hasInterpolatedSlices && (
                <Tooltip
                    title={
                        <FormattedMessage
                            id="image.interpolatedSlices.tooltip"
                            values={{
                                count: metadata.interpolatedSliceCount,
                                total: metadata.length,
                            }}
                        />
                    }
                    arrow
                    placement="top"
                    slotProps={tooltipAboveTooltipsSlotProps}
                >
                    <Box
                        component="span"
                        display="inline-flex"
                        alignItems="center"
                        role="img"
                        tabIndex={0}
                        sx={{
                            color: 'warning.main',
                            cursor: 'help',
                            outline: 'none',
                            '&:focus-visible': {
                                outline: '1px solid',
                                outlineColor: 'warning.main',
                                outlineOffset: 2,
                                borderRadius: '50%',
                            },
                        }}
                        aria-label={intl.formatMessage(
                            {
                                id: 'image.interpolatedSlices.tooltip',
                            },
                            {
                                count: metadata.interpolatedSliceCount,
                                total: metadata.length,
                            }
                        )}
                    >
                        <Info size={INFO_ICON_SIZE} />
                    </Box>
                </Tooltip>
            )}
            {hasSharedFrameOfReference && (
                <Chip
                    label="FOR"
                    size="small"
                    color="primary"
                    sx={compactChipSx}
                />
            )}
            {obliqueAngle && (
                <Tooltip
                    title={intl.formatMessage(
                        { id: 'image.oblique.tooltip' },
                        { angle: obliqueAngle }
                    )}
                    arrow
                    placement="top"
                    slotProps={tooltipAboveTooltipsSlotProps}
                >
                    <Chip
                        label={intl.formatMessage(
                            { id: 'image.oblique.chip' },
                            { angle: obliqueAngle }
                        )}
                        size="small"
                        color="warning"
                        sx={compactChipSx}
                    />
                </Tooltip>
            )}
        </>
    );
}
